import type { ComponentType } from 'react';
import { redirect, type RouteObject } from 'react-router';

import { restoreSessionLoader } from '../api/sessionRestore';
import { AppLoading, RootLayout } from './RootLayout';
import { NotFoundPage, RouteError } from './RouteError';
import { recoverFromStaleChunk } from './staleChunk';

/**
 * Carga diferida de un componente. Cada pantalla y cada layout es su propio chunk: el shell inicial
 * solo trae React, el router y esta configuración, y una ruta descarga únicamente lo que usa.
 * Si el chunk ya no existe (deploy nuevo con el shell viejo), `recoverFromStaleChunk` recarga limpio.
 */
function lazyComponent<Module extends Record<string, unknown>>(load: () => Promise<Module>, name: keyof Module & string) {
  return async () => {
    try {
      const mod = await load();
      return { Component: mod[name] as ComponentType };
    } catch (error) {
      // Chunk de un deploy anterior: se limpia y se recarga; mientras tanto la ruta sigue cargando
      if (await recoverFromStaleChunk(error)) return new Promise<never>(() => undefined);
      throw error;
    }
  };
}

const design = () => import('../routes/design/pages');

/** Mensaje de invitación: lo usa el botón "Invitar" de la plantilla de WhatsApp (Meta no admite enlaces wa.me directos). */
const SHARE_TEXT =
  '¡Únete conmigo a El Parche Cafetero! La Ruta del Café de Fusagasugá arranca el 23 de octubre, con bebidas de autor y premios. Entra aquí: https://elparchecafetero.com';

export const routes: RouteObject[] = [
  {
    Component: RootLayout,
    ErrorBoundary: RouteError,
    HydrateFallback: AppLoading,
    // Recupera la sesión (cookie de refresh) antes de pintar la primera pantalla.
    loader: restoreSessionLoader,
    shouldRevalidate: () => false,
    children: [
      // Cliente: landing sin navegación de app
      { index: true, lazy: lazyComponent(() => import('../routes/client/landing/LandingPage'), 'LandingPage') },
      // Cliente: abre el selector de WhatsApp con el mensaje de invitación (botón de la plantilla)
      { path: 'invitar', loader: () => redirect(`https://wa.me/?text=${encodeURIComponent(SHARE_TEXT)}`) },
      // El agente de WhatsApp mandó este enlace a comercios: sigue vivo en esos chats.
      { path: 'inscribirme', loader: () => redirect('/estacion/inscripcion') },
      // Cliente: enlace mágico de ingreso que llega por WhatsApp (`/acceso?t=…`)
      { path: 'acceso', lazy: lazyComponent(() => import('../routes/client/access/AccessPage'), 'AccessPage') },
      // Cliente: pantallas con bottom nav (mobile) o barra superior (desktop)
      {
        lazy: lazyComponent(() => import('../layouts/client/ClientLayout'), 'ClientLayout'),
        children: [
          { path: 'mapa', lazy: lazyComponent(() => import('../routes/client/map/MapPage'), 'MapPage') },
          { path: 'ruta', lazy: lazyComponent(() => import('../routes/client/route/RoutePage'), 'RoutePage') },
          { path: 'ranking', lazy: lazyComponent(() => import('../routes/client/ranking/RankingPage'), 'RankingPage') },
          { path: 'pasaporte', lazy: lazyComponent(() => import('../routes/client/passport/PassportPage'), 'PassportPage') },
          { path: 'perfil', lazy: lazyComponent(() => import('../routes/client/profile/ProfilePage'), 'ProfilePage') },
        ],
      },
      // Cliente: detalle, bebida y confirmación (sin bottom nav)
      {
        lazy: lazyComponent(() => import('../layouts/client/ClientLayout'), 'ClientDetailLayout'),
        children: [
          { path: 'estaciones/:slug', lazy: lazyComponent(() => import('../routes/client/station/StationPage'), 'StationPage') },
          { path: 'estaciones/:slug/bebida', lazy: lazyComponent(() => import('../routes/client/beverage/BeveragePage'), 'BeveragePage') },
          { path: 'voto/confirmado', lazy: lazyComponent(() => import('../routes/client/vote/VoteConfirmedPage'), 'VoteConfirmedPage') },
          // Landing pública de ganadores de los ciclos de premios por sellos
          { path: 'ganadores', lazy: lazyComponent(() => import('../routes/client/winners/WinnersPage'), 'WinnersPage') },
          // Páginas de contenido publicadas desde admin (términos, privacidad y otras)
          { path: 'terminos', lazy: lazyComponent(() => import('../routes/client/content/ContentPage'), 'TermsPage') },
          { path: 'privacidad', lazy: lazyComponent(() => import('../routes/client/content/ContentPage'), 'PrivacyPage') },
          { path: 'contenido/:slug', lazy: lazyComponent(() => import('../routes/client/content/ContentPage'), 'ContentSlugPage') },
        ],
      },
      // Panel de estación
      {
        path: 'estacion',
        children: [
          { path: 'ingresar', lazy: lazyComponent(() => import('../routes/station/login/StationLoginPage'), 'StationLoginPage') },
          // Enlace mágico de ingreso que llega por WhatsApp (`/estacion/acceso?t=…`)
          { path: 'acceso', lazy: lazyComponent(() => import('../routes/station/access/StationAccessPage'), 'StationAccessPage') },
          { path: 'inscripcion/:paso?', lazy: lazyComponent(() => import('../routes/station/signup/StationSignupPage'), 'StationSignupPage') },
          {
            lazy: lazyComponent(() => import('../layouts/station/StationLayout'), 'StationLayout'),
            children: [
              { index: true, lazy: lazyComponent(() => import('../routes/station/summary/StationSummaryPage'), 'StationSummaryPage') },
              { path: 'perfil', lazy: lazyComponent(() => import('../routes/station/profile/StationProfilePage'), 'StationProfilePage') },
              { path: 'bebida', lazy: lazyComponent(() => import('../routes/station/beverage/StationBeveragePage'), 'StationBeveragePage') },
              { path: 'ranking', lazy: lazyComponent(() => import('../routes/station/ranking/StationRankingPage'), 'StationRankingPage') },
              { path: 'resenas', lazy: lazyComponent(() => import('../routes/station/reviews/StationReviewsPage'), 'StationReviewsPage') },
              { path: 'estadisticas', lazy: lazyComponent(() => import('../routes/station/stats/StationStatsPage'), 'StationStatsPage') },
              { path: 'visitas', lazy: lazyComponent(() => import('../routes/station/visits/StationVisitsPage'), 'StationVisitsPage') },
              { path: 'votos', lazy: lazyComponent(() => import('../routes/station/votes/StationVotesPage'), 'StationVotesPage') },
              { path: 'galeria', lazy: lazyComponent(() => import('../routes/station/gallery/StationGalleryPage'), 'StationGalleryPage') },
              { path: 'configuracion', lazy: lazyComponent(() => import('../routes/station/settings/StationSettingsPage'), 'StationSettingsPage') },
            ],
          },
        ],
      },
      // Administración
      {
        path: 'admin',
        children: [
          { path: 'ingresar', lazy: lazyComponent(() => import('../routes/admin/login/AdminLoginPage'), 'AdminLoginPage') },
          {
            lazy: lazyComponent(() => import('../layouts/admin/AdminLayout'), 'AdminLayout'),
            children: [
              { index: true, lazy: lazyComponent(() => import('../routes/admin/dashboard/AdminDashboardPage'), 'AdminDashboardPage') },
              { path: 'estaciones', lazy: lazyComponent(() => import('../routes/admin/stations/AdminStationsPage'), 'AdminStationsPage') },
              { path: 'estaciones/:id', lazy: lazyComponent(() => import('../routes/admin/station-detail/AdminStationDetailPage'), 'AdminStationDetailPage') },
              { path: 'votos', lazy: lazyComponent(() => import('../routes/admin/votes/AdminVotesPage'), 'AdminVotesPage') },
              { path: 'resenas', lazy: lazyComponent(() => import('../routes/admin/reviews/AdminReviewsPage'), 'AdminReviewsPage') },
              { path: 'visitas', lazy: lazyComponent(() => import('../routes/admin/visits/AdminVisitsPage'), 'AdminVisitsPage') },
              { path: 'ranking', lazy: lazyComponent(() => import('../routes/admin/ranking/AdminRankingPage'), 'AdminRankingPage') },
              { path: 'premios', lazy: lazyComponent(() => import('../routes/admin/prizes/AdminPrizesPage'), 'AdminPrizesPage') },
              { path: 'clientes', lazy: lazyComponent(() => import('../routes/admin/clients/AdminClientsPage'), 'AdminClientsPage') },
              { path: 'analitica', lazy: lazyComponent(() => import('../routes/admin/analytics/AdminAnalyticsPage'), 'AdminAnalyticsPage') },
              { path: 'bebidas', lazy: lazyComponent(() => import('../routes/admin/beverages/AdminBeveragesPage'), 'AdminBeveragesPage') },
              { path: 'pasaportes', lazy: lazyComponent(() => import('../routes/admin/passports/AdminPassportsPage'), 'AdminPassportsPage') },
              { path: 'contenido', lazy: lazyComponent(() => import('../routes/admin/content/AdminContentPage'), 'AdminContentPage') },
              { path: 'auditoria', lazy: lazyComponent(() => import('../routes/admin/audit/AdminAuditPage'), 'AdminAuditPage') },
              { path: 'configuracion', lazy: lazyComponent(() => import('../routes/admin/settings/AdminSettingsPage'), 'AdminSettingsPage') },
            ],
          },
        ],
      },
      // Referencia interna: design system (réplica de DesignSystem.html) y vitrina de componentes
      { path: '_design', lazy: lazyComponent(design, 'DesignSystemPage') },
      { path: '_design/componentes', lazy: lazyComponent(design, 'ComponentsPage') },
      { path: '*', Component: NotFoundPage },
    ],
  },
];
